/**
 * Centralized best score management for all games
 */

import { BestScoreStorage } from './bestScoreStorage.js';

// Game types
export const GAME_TYPES = [
  'solitaire',
  'sudoku',
  '2048',
  'minesweeper',
  'tetris',
  'snake',
  'blockdestroyer',
  'sort-puzzle',
  'match-3',
  'memory-game',
  'quick-math',
  'pattern-challenge',
  'maze',
  'forest-runner',
  'endless-shred',
] as const;

export type GameType = (typeof GAME_TYPES)[number];

export function isGameType(value: unknown): value is GameType {
  return GAME_TYPES.some((game) => game === value);
}

// Score type for each game
export type ScoreType = 'high-score' | 'low-time';

// Configuration for each game
const GAME_CONFIG: Record<GameType, { scoreType: ScoreType; storageKey: string }> = {
  'solitaire': { scoreType: 'high-score', storageKey: 'solitaire-best-score' },
  'sudoku': { scoreType: 'low-time', storageKey: 'sudoku-best-time' },
  '2048': { scoreType: 'high-score', storageKey: '2048-best-score' },
  'minesweeper': { scoreType: 'low-time', storageKey: 'minesweeper-best-time' },
  'tetris': { scoreType: 'high-score', storageKey: 'tetris-best-score' },
  'snake': { scoreType: 'high-score', storageKey: 'snake-best-score' },
  'blockdestroyer': { scoreType: 'high-score', storageKey: 'blockdestroyer-best-score' },
  'sort-puzzle': { scoreType: 'high-score', storageKey: 'sort-puzzle' },
  'match-3': { scoreType: 'high-score', storageKey: 'match-3-best-score' },
  'memory-game': { scoreType: 'high-score', storageKey: 'memory-game-best-score' },
  'quick-math': { scoreType: 'high-score', storageKey: 'quick-math-best-score' },
  'pattern-challenge': { scoreType: 'high-score', storageKey: 'pattern-challenge-best-score' },
  'maze': { scoreType: 'high-score', storageKey: 'maze-best-score' },
  'forest-runner': { scoreType: 'high-score', storageKey: 'forest-runner-best-score' },
  'endless-shred': { scoreType: 'high-score', storageKey: 'endless-shred-best-score' }
};

// Storage instances for each game
const storageInstances = new Map<GameType, BestScoreStorage>();

function getStorage(game: GameType): BestScoreStorage {
  if (!storageInstances.has(game)) {
    storageInstances.set(game, new BestScoreStorage(game));
  }
  return storageInstances.get(game)!;
}

/**
 * Update best score for a game if the new score is better
 * @param game The game identifier
 * @param value The score or time value
 * @returns true if this is a new best score, false otherwise
 */
export function updateBestScore(game: GameType, value: number): boolean {
  const config = GAME_CONFIG[game];
  const storage = getStorage(game);
  const currentBest = storage.get();

  let isNewBest = false;

  if (config.scoreType === 'high-score') {
    // Higher is better
    if (value > currentBest) {
      storage.set(value);
      isNewBest = true;
    }
  } else {
    // Lower is better (time)
    if (currentBest === 0 || value < currentBest) {
      storage.set(value);
      isNewBest = true;
    }
  }

  return isNewBest;
}

/**
 * Get the best score for a game
 * @param game The game identifier
 * @returns The best score or time, or 0 if none exists
 */
export function getBestScore(game: GameType): number {
  return getStorage(game).get();
}

/**
 * Format the best score for display
 * @param game The game identifier
 * @returns Formatted string for display
 */
export function formatBestScore(game: GameType): string {
  const value = getBestScore(game);
  const config = GAME_CONFIG[game];

  if (value === 0) {
    return config.scoreType === 'high-score' ? '0' : '--';
  }

  if (config.scoreType === 'high-score') {
    return value.toLocaleString();
  } else {
    // Format time as MM:SS
    const minutes = Math.floor(value / 60);
    const seconds = value % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

/**
 * Clear best score for a game
 * @param game The game identifier
 */
export function clearBestScore(game: GameType): void {
  getStorage(game).clear();
}
